/**
 * @fileoverview WEB-806 — source-accurate headings for event info copy.
 *
 * Ticketmaster does not publish a description. The string stored in
 * `event.description` for TM rows is venue policy (`info` / `pleaseNote`),
 * so rendering it under "Description" or "About" would manufacture a
 * summary the source never wrote. This module classifies copy by source
 * and (once PIP-232 lands) by named fields, and is the single place that
 * decision is stated — the spine molecule, `generateMetadata`, and
 * `buildEventJsonLd` all read from here.
 *
 * WEB-844: for Ticketmaster rows ONLY, the two policy fields are de-duplicated
 * (TM's `info` is routinely a prefix of, or identical to, `pleaseNote`) and run
 * through `formatPolicyText`, which restores the one-sentence-per-line structure
 * the public Discovery API flattens. Other sources render these fields verbatim,
 * and the `about` block is exempt everywhere — see the note at its push site.
 *
 * PIP-247: the PIP-232 columns (`importantInfo`, `additionalInfo`) ARE on
 * `EventDetailV2` — it is `allOf: [Event, ...]`, and `Event` declares both
 * (`specs/openapi/backend-api-v1.yaml`), so the generated type intersects them
 * in. They are therefore read as typed properties, NOT via `Reflect.get`.
 * That matters: a structural read of a field the contract had dropped would
 * return `undefined` silently, the de-duplication below would stop firing, and
 * the duplicate-policy render this module exists to prevent would come back
 * with no compile error. Typed access makes that a build failure instead.
 */

import { formatPolicyText } from '@/lib/eventDetail/formatPolicyText';
import type { EventDetailV2 } from '@/types/eventDetail';

export const EVENT_INFO_HEADING = 'Event Info';
export const ADDITIONAL_INFO_HEADING = 'Additional Info';
/**
 * FRE-1313 removed the `about` block kind from this module. Body copy is no
 * longer info-block copy: it is the About column, whose heading comes from the
 * category lexicon's `aboutHeading` ('About this show', 'About this
 * exhibition', … and 'About this event' for the uncategorised fallback). A
 * constant here would be a second definition of a string the lexicon already
 * owns.
 */
export type EventSourceInfoKind = 'eventInfo' | 'additionalInfo';

export interface EventSourceInfoBlock {
  kind: EventSourceInfoKind;
  heading: string;
  body: string;
}

/** Canonical Discovery-API source slug. Exported so Event v2 stories/specs can
 *  classify TM rows without embedding the denylisted proper noun (WEB-721). */
export const TICKETMASTER_SOURCE = 'ticketmaster';

function sourceName(event: EventDetailV2): string {
  const name = event.source?.name;
  return typeof name === 'string' ? name.trim().toLowerCase() : '';
}

function isTicketmasterSource(name: string): boolean {
  return name === TICKETMASTER_SOURCE;
}

/**
 * Canonical `EventSource.name` values for lanes that can publish an organizer.
 *
 * Exactly one today. Both Eventbrite discovery lanes — the Apify actor and the
 * PIP-259 city-directory sweep — converge on the same `'eventbrite'`
 * `EventSource` row, because the directory lane emits actor-shaped items
 * through the same persister; and `Event.primaryOrganizerName` /
 * `primaryOrganizerUrl` are written from nowhere else (the Apify persister,
 * the organizer-page parser, and the detail backfill, all Eventbrite).
 *
 * Ticketmaster, UNC-Charlotte, CRVA, venue sites, seeds, and hand-created rows
 * have no organizer concept at all, which is a different thing from "published
 * none" and is the distinction {@link publishesOrganizer} exists to draw.
 */
const ORGANIZER_PUBLISHING_SOURCES: ReadonlySet<string> = new Set(['eventbrite']);

/**
 * True when this event's source is a lane that publishes an organizer — so
 * NOT holding one is a real, reportable gap rather than a category error.
 *
 * Exported because source classification belongs in exactly one file — this
 * one — and WEB-855 needs the verdict outside it: `resolveEventModules` drops
 * the `people` slot entirely for a lane that could never fill it, instead of
 * printing a degrade note claiming a gap that cannot exist. Re-deriving
 * `event.source?.name === 'eventbrite'` at the call site would be a second,
 * case-sensitive opinion on a question this module already answers (the
 * trim + lowercase in {@link sourceName} is why `'  Eventbrite '` classifies
 * correctly).
 *
 * Takes the payload rather than a pre-normalised name so that normalisation
 * cannot be forgotten by a caller. An absent, null, or non-string
 * `source.name` resolves `''` and is therefore not organizer-publishing —
 * the right answer for a hand-created row, whose `sourceId` is nullable.
 */
export function publishesOrganizer(event: EventDetailV2): boolean {
  return ORGANIZER_PUBLISHING_SOURCES.has(sourceName(event));
}

function trimmedNonEmpty(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Read one PIP-232 policy column off the generated contract type. Returns null
 * when the field is absent, null, or whitespace-only.
 */
function readNamedInfoField(
  event: EventDetailV2,
  field: 'importantInfo' | 'additionalInfo',
): string | null {
  return trimmedNonEmpty(event[field]);
}

function trimmedDescription(event: EventDetailV2): string | null {
  return trimmedNonEmpty(event.description);
}

/**
 * Single-line form: every run of whitespace collapses to one space. Used both
 * to compare policy fields on wording rather than spacing, and to flatten a
 * multi-paragraph body into the one line an SEO summary has to be.
 */
function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/** Whitespace-insensitive form, so overlap is detected on wording not spacing. */
const normalizeForComparison = collapseWhitespace;

/**
 * True when `value` starts with the whole of `prefix` AND ends that prefix on a
 * word boundary — i.e. the two are equal, or the next character is the single
 * space `normalizeForComparison` guarantees between words.
 *
 * A bare `startsWith` is not sufficient: "Parking is $100 for buses." starts
 * with "Parking is $10", so suppressing on a raw character prefix would hide a
 * genuinely different policy. Suppression must only ever drop text the surviving
 * value actually restates.
 */
function containsAsLeadingPolicy(value: string, prefix: string): boolean {
  return value === prefix || value.startsWith(`${prefix} `);
}

interface PolicyPair {
  /** Rendered as the Event Info block. */
  primary: string | null;
  /** Rendered as the Additional Info block; null when it would duplicate. */
  secondary: string | null;
}

/**
 * WEB-844 / PIP-247 — collapse overlapping Ticketmaster policy fields.
 *
 * TM's `info` and `pleaseNote` routinely overlap: on the reference event
 * `info` is a strict prefix of `pleaseNote`, and on another live event the two
 * are byte-identical. Rendering both then prints the same sentences twice
 * under two headings.
 *
 * When one contains the other we therefore render the SUPERSET once, under the
 * Event Info heading — "Additional Info" is meaningless with nothing to be
 * additional to, and the superset already carries every word of the subset, so
 * no source copy is lost. Genuinely divergent fields still render as two
 * blocks.
 */
function dedupePolicyPair(importantInfo: string | null, additionalInfo: string | null): PolicyPair {
  if (importantInfo === null || additionalInfo === null) {
    return { primary: importantInfo, secondary: additionalInfo };
  }

  const important = normalizeForComparison(importantInfo);
  const additional = normalizeForComparison(additionalInfo);

  if (containsAsLeadingPolicy(additional, important)) {
    return { primary: additionalInfo, secondary: null };
  }
  if (containsAsLeadingPolicy(important, additional)) {
    return { primary: importantInfo, secondary: null };
  }

  return { primary: importantInfo, secondary: additionalInfo };
}

/**
 * Ordered, self-gated info blocks for the event spine. Empty array means
 * render nothing — never a heading over an empty body, never fabricated copy.
 */
export function resolveEventSourceInfoBlocks(event: EventDetailV2): EventSourceInfoBlock[] {
  const blocks: EventSourceInfoBlock[] = [];
  const name = sourceName(event);
  const importantInfo = readNamedInfoField(event, 'importantInfo');
  const additionalInfo = readNamedInfoField(event, 'additionalInfo');

  // `importantInfo` / `additionalInfo` are PIP-232 columns written ONLY by the
  // Ticketmaster transformer — no other ingestion lane populates them. The
  // de-duplication and line reconstruction below are therefore scoped to that
  // source, so a lane that later starts writing these fields cannot silently
  // have prose suppressed or sentence-split as though it were policy copy.
  if (isTicketmasterSource(name)) {
    const { primary, secondary } = dedupePolicyPair(importantInfo, additionalInfo);

    if (primary) {
      blocks.push({
        kind: 'eventInfo',
        heading: EVENT_INFO_HEADING,
        body: formatPolicyText(primary),
      });
    }

    if (secondary) {
      blocks.push({
        kind: 'additionalInfo',
        heading: ADDITIONAL_INFO_HEADING,
        body: formatPolicyText(secondary),
      });
    }
  } else {
    if (importantInfo) {
      blocks.push({ kind: 'eventInfo', heading: EVENT_INFO_HEADING, body: importantInfo });
    }
    if (additionalInfo) {
      blocks.push({
        kind: 'additionalInfo',
        heading: ADDITIONAL_INFO_HEADING,
        body: additionalInfo,
      });
    }
  }

  const description = trimmedDescription(event);
  if (!description) {
    return blocks;
  }

  if (isTicketmasterSource(name)) {
    // Interim: until PIP-232 lands, TM `description` is policy. Do not also
    // render it as Event Info when the named `importantInfo` field is present.
    if (!importantInfo) {
      blocks.push({
        kind: 'eventInfo',
        heading: EVENT_INFO_HEADING,
        body: formatPolicyText(description),
      });
    }
    return blocks;
  }

  // FRE-1313 — every other source's `description` is the event's own body
  // copy, and it renders in the About column (`resolveAboutColumnBody` below),
  // NOT here. This module used to push an `about` block for it, and the spine
  // mounts this molecule directly above the module tier in the same editorial
  // column — so once the About column existed, a UNC-Charlotte or
  // unknown-source description printed twice on one page, under two different
  // About headings. One string, one renderer.
  return blocks;
}

/**
 * Minimum collapsed length for a string to be emitted as a summary.
 *
 * FRE-1319 — this is a floor on the DEGENERATE case, not a teaser classifier.
 * A prod census of every non-Ticketmaster row with a description found the
 * sub-40 band is almost entirely copy that does not summarise anything: bare
 * lineup strings (a support act's name, or "<performer> with <performer>"),
 * door-policy fragments ("* 21+ with valid ID | Doors @ 7pm"), scraper
 * chrome ("View on site | Email this event"), and outright junk ("H", "....").
 * Real summaries start at ~40 chars — the 5th percentile of the un-hydrated
 * Eventbrite population — so the cut costs 3.3% of Eventbrite rows and 0.2% of
 * UNC-Charlotte ones while keeping every one of those strings out of a
 * `<meta name="description">`.
 *
 * Length is a proxy for "is this a summary", so the cut is imperfect in both
 * directions and is deliberately set where the junk cluster ends rather than
 * tuned to catch every case. The title-based fallback is the honest answer
 * below it.
 */
export const MIN_SUMMARY_CHARS = 40;

/**
 * FRE-1313 — the body copy the About column may render, or `null` when this
 * event has none.
 *
 * Source classification is the entire point, and it belongs here rather than
 * in the module selector: `event.description` is not one kind of string. For
 * Ticketmaster it is venue policy (`info` / `pleaseNote`), which
 * {@link resolveEventSourceInfoBlocks} already renders under "Event Info" a
 * few DOM nodes higher up the same column. Gating the About column on a bare
 * `description.trim().length > 0` therefore printed that policy text a second
 * time, under a heading claiming it was a summary the source never wrote —
 * the exact mislabelling this module was created (WEB-806) to prevent.
 *
 * Eventbrite IS included: its `description` is the organizer body, which is
 * the whole reason the About column exists (FRE-1313). This module skips it
 * only because a body is not info-block copy, not because it is untrustworthy.
 *
 * This is the About column's only gate — `hasModuleContent('about', …)` is
 * `resolveAboutColumnBody(event) !== null`, so the rule has exactly one home.
 */
export function resolveAboutColumnBody(event: EventDetailV2): string | null {
  const description = trimmedDescription(event);
  if (!description) {
    return null;
  }
  // Policy, not prose. Already rendered as Event Info above.
  if (isTicketmasterSource(sourceName(event))) {
    return null;
  }
  // FRE-1320 — same quality floor as resolveGenuineEventDescription: bare
  // lineup strings, door-policy fragments, and scraper chrome are under
  // MIN_SUMMARY_CHARS on the collapsed length. Prefer an absent About module
  // over printing that junk under "About this show". Return the trimmed
  // original so multi-paragraph bodies still split correctly.
  if (collapseWhitespace(description).length < MIN_SUMMARY_CHARS) {
    return null;
  }
  return description;
}

/**
 * The one string that is honest to emit as a description / summary on SEO
 * surfaces (`generateMetadata`, `buildEventJsonLd`), or null. Ticketmaster
 * policy and copy too short to summarise anything return null so
 * meta/OG/Twitter/JSON-LD fall back to title-based copy (or omit the field)
 * instead of labelling policy as a summary.
 *
 * FRE-1319 — Eventbrite is no longer excluded by source. PIP-233's standing
 * position was that its persisted `description` is a ~140-char listing teaser
 * unfit to be a summary; a prod census retired that premise. The short strings
 * are complete, organizer-written summary sentences with terminal punctuation
 * ("Join us for Music Bingo every Wednesday at 7:30 PM. Great music, fun, and
 * prizes await!" is the shape, verbatim but for the venue), not mid-sentence
 * truncations — 9 of 3,179 rows end in an ellipsis — and PIP-238 hydration has
 * since given a third of them the full organizer body anyway. Both branches are
 * capped at the same 160 chars by the callers, so the surviving difference
 * between a "teaser" and a "body" is invisible on this surface.
 *
 * The result is whitespace-collapsed because hydrated bodies are
 * multi-paragraph: 67% of them carry a newline inside the first 160 chars, and
 * neither caller's truncator collapses whitespace, so returning the raw string
 * would put paragraph breaks in meta tags and JSON-LD and spend the snippet
 * budget on whitespace.
 *
 * FRE-1320 aligned {@link resolveAboutColumnBody} on the same length floor:
 * short lineup / door-policy / junk strings mount neither the About column
 * nor SEO summary surfaces. Remaining divergence is presentation only —
 * About returns the trimmed multi-paragraph body; this helper collapses
 * whitespace for meta / JSON-LD.
 */
export function resolveGenuineEventDescription(event: EventDetailV2): string | null {
  const description = trimmedDescription(event);
  if (!description) {
    return null;
  }
  // Policy, not prose — the WEB-806 rule this module exists for.
  if (isTicketmasterSource(sourceName(event))) {
    return null;
  }
  const summary = collapseWhitespace(description);
  return summary.length >= MIN_SUMMARY_CHARS ? summary : null;
}
